import React, { useEffect, useState } from 'react';
import './ReceiptPopup.scss';
import { Col, ModalBody, Row } from 'reactstrap';
import logoConnect from '../../assets/demo-ui/images/VetValueConnect.png';
import { useStateSelector } from '../../store/selectors';
import createNotification from '../../utils/createNotification';
import Rodal from '../Rodal/Rodal';
import { Transaction } from '../../wallet/models/Transaction';
import { PaymentTypes } from '../../wallet/models/TransactionPayment';
import { ReceiptLine } from '../../wallet/TransactionReceipt';
import ButtonLoader from '../Layout/Buttons/ButtonLoader';
import { TransactionSource } from '../../wallet/models/TransactionSource';
import Loader from 'react-loaders';
import InfoTooltip from '../info-tooltip/info-tooltip';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../routing/path';
import moment from 'moment';

interface ReceiptPopupProps {
    isVisible: boolean;
    onClose: () => void;
    transactionId?: string;
    invoiceIds?: string[];
    mode: ReceiptViewMode;
    userId: number;
    isThirdPartyView?: boolean;
    canNavigateToTransaction?: boolean;
}

export enum ReceiptViewMode {
    TransactionView,
    InvoicesView,
}

export const ReceiptPopup = (props: ReceiptPopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [doesReceiptExist, setDoesReceiptExist] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (props.isVisible) {
            if (
                props.transactionId &&
                props.mode == ReceiptViewMode.TransactionView
            ) {
                fetchTransactionById();
            } else if (
                props.invoiceIds &&
                props.invoiceIds.length &&
                props.mode == ReceiptViewMode.InvoicesView
            ) {
                fetchTransactionsByInvoiceIds();
            }
        }
    }, [props.transactionId, props.isVisible]);

    const fetchTransactionById = () => {
        const params = new URLSearchParams();

        if (props.isThirdPartyView) {
            params.append('userId', props.userId?.toString() ?? '');
        }

        axios
            .get(`/api/wallet/transactions/${props.transactionId}`, { params })
            .then((response) => {
                if (response.status !== 200) {
                    createNotification('Error fetching data', 'error');
                    return;
                }
                setTransactions([response.data]);
                const receipts = response.data?.receipts;

                if (receipts?.length) {
                    setDoesReceiptExist(true);
                } else {
                    props.onClose();
                }
            });
    };

    const fetchTransactionsByInvoiceIds = () => {
        const params = new URLSearchParams();
        props.invoiceIds.forEach((id) => params.append('invoiceIds', id));

        if (props.isThirdPartyView) {
            params.append('userId', props.userId?.toString() ?? '');
        }

        axios
            .get(`/api/wallet/transactions/getByInvoices?`, { params })
            .then((response) => {
                if (response.status !== 200) {
                    createNotification('Error fetching data', 'error');
                    props.onClose();
                    return;
                }
                setTransactions(response.data);
                const trxs = response.data as Transaction[];
                const receipts = trxs?.map((trx) => trx.receipts);

                if (receipts?.length) {
                    setDoesReceiptExist(true);
                } else {
                    props.onClose();
                }
            });
    };

    const fetchReceiptByTransactionId = () => {
        setIsLoading(true);

        const params = new URLSearchParams();
        if (props.isThirdPartyView) {
            params.append('userId', props.userId?.toString() ?? '');
        }

        axios
            .get(`/api/Receipts/${props.transactionId}`, {
                responseType: 'blob',
                params,
            })
            .then((response) => {
                if (response.status !== 200) {
                    createNotification('Error downloading receipt', 'error');
                    return;
                }
                const d = new Date();
                const stamp = moment(d).format('L');

                const fileName = `${props.transactionId}_${stamp}.pdf`;

                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchReceiptByTransactionIds = () => {
        setIsLoading(true);
        const transactionIds = transactions.map((t) => t.id);
        const params = new URLSearchParams();
        transactionIds.forEach((id) => params.append('transactionIds', id));

        if (props.isThirdPartyView) {
            params.append('userId', props.userId?.toString() ?? '');
        }

        axios
            .get('/api/Receipts', {
                responseType: 'blob',
                params,
            })
            .then((response) => {
                if (response.status !== 200) {
                    createNotification('Error downloading receipt', 'error');
                    return;
                }
                const d = new Date();
                const stamp = moment(d).format('L');
                const transactionId = transactions.find((x) => x.id)?.id;
                const fileName = `${transactionId}_${stamp}.pdf`;

                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const money = (v: number) =>
        (v ?? 0).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        });

    const generateHeader = () => {
        return (
            <div className="receipt-header">
                <div className="receipt-header__logo">
                    <img
                        width={169}
                        height={82}
                        alt="logoConnect"
                        src={logoConnect}
                    />
                </div>
                <div className="receipt-header__title">VV Connect</div>
                <div className="receipt-header__title">Fee Receipt</div>
            </div>
        );
    };

    const renderTooltip = (id: string, text: string) => {
        return <InfoTooltip idText={id} text={text} />;
    };

    const generateReceiptLines = (transaction: Transaction) => {
        const rec = transaction?.receipts && transaction.receipts[0];
        let lines = (rec?.descriptionLines ?? []).slice();

        if (!lines.length) {
            let type = transaction?.type;
            lines = [
                {
                    label: TransactionSource.find((t) => t.id == type)?.value,
                    value: '',
                    rowPosition: 1,
                    columnPosition: 1,
                    id: 0,
                    tooltipText: '',
                },
            ];
        }

        lines.sort((a, b) => {
            if (a.rowPosition === b.rowPosition)
                return a.columnPosition - b.columnPosition;
            return a.rowPosition - b.rowPosition;
        });

        const firstRow = Math.min(...lines.map((x) => x.rowPosition));
        const lastRow = Math.max(...lines.map((x) => x.rowPosition));

        const rows: { key: number; items: ReceiptLine[] }[] = [];
        for (const l of lines) {
            const found = rows.find((r) => r.key === l.rowPosition);
            if (found) found.items.push(l);
            else rows.push({ key: l.rowPosition, items: [l] });
        }

        return (
            <div className="receipt-lines">
                {rows.map((r) => {
                    const isFirst = r.key === firstRow;
                    const isLast = r.key === lastRow;
                    const cols = r.items.length;
                    const mdSize = cols === 2 ? 6 : 12;

                    return (
                        <Row
                            key={`row-${r.key}`}
                            className={isFirst ? 'mb-3' : 'mb-1'}>
                            {r.items.map((it, idx) => (
                                <Col
                                    md={mdSize}
                                    key={`cell-${r.key}-${idx}`}
                                    className="receipt-line-item">
                                    <span
                                        className={`label ${
                                            isFirst || isLast ? 'bold' : ''
                                        }`}>
                                        {it.label}
                                        {it.tooltipText ? (
                                            renderTooltip(
                                                `cell-${r.key}-${idx}`,
                                                it.tooltipText
                                            )
                                        ) : (
                                            <></>
                                        )}
                                        {isFirst ? ' ' : ': '}
                                        {it.value ?? ''}
                                    </span>
                                </Col>
                            ))}
                        </Row>
                    );
                })}
            </div>
        );
    };

    const generateTransactionDetails = (transaction: Transaction) => {
        if (!transaction) return null;

        const payments = transaction.payments || [];
        const sum = (t: PaymentTypes) =>
            payments
                .filter((p) => p.type === t)
                .reduce((a, b) => a + Math.abs(b.amount || 0), 0);

        const totalStore = sum(PaymentTypes.StoreCredits);
        const totalReward = sum(PaymentTypes.RewardCredits);
        const totalFlex = sum(PaymentTypes.FlexCredits);
        const totalCard = sum(PaymentTypes.CreditCard);
        const lastDigits =
            payments.find((p) => p.type === PaymentTypes.CreditCard)
                ?.lastDigits || '';
        const total = payments.reduce((a, b) => a + Math.abs(b.amount || 0), 0);

        const rows = [
            { label: 'Store Credits:', value: money(totalStore), bold: false },
            { label: 'Flex Credits:', value: money(totalFlex), bold: false },
            {
                label: 'Reward Credits:',
                value: money(totalReward),
                bold: false,
            },
            {
                label: `CC #${lastDigits}:`,
                value: money(totalCard),
                bold: false,
            },
            { label: 'Total:', value: money(total), bold: true },
        ];

        return (
            <div className="receipt-details">
                <Row className="mb-1">
                    <Col md={12}>
                        <span className="label">Payment Date: </span>
                        <span className="value">
                            {new Date(transaction.date).toLocaleDateString(
                                'en-US'
                            )}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-1">
                    <Col md={12}>
                        <span className="label">Transaction Number: </span>
                        <span
                            className="value link-text"
                            onClick={() => {
                                if (
                                    !props.isThirdPartyView &&
                                    props.canNavigateToTransaction
                                ) {
                                    navigateToWalletTransaction(transaction);
                                }
                            }}>
                            {transaction.id}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-1">
                    <Col md={12}>Payment method:</Col>
                </Row>

                {rows.map((r, i) => (
                    <Row key={`pay-${i}`} className="ml-1 mb-1">
                        <Col md={4}>
                            <span className={`label ${r.bold ? 'bold' : ''}`}>
                                {r.label}
                            </span>
                        </Col>
                        <Col
                            md={4}
                            className={`text-left ${r.bold ? 'bold' : ''}`}>
                            {r.value}
                        </Col>
                    </Row>
                ))}
            </div>
        );
    };

    const navigateToWalletTransaction = (transaction: Transaction) => {
        navigate(`${paths.account.profile}?transactionId=${transaction.id}`);
    };

    const generateFooter = () => {
        return (
            <div className="receipt-footer">
                <button className="btn btn-secondary" onClick={props.onClose}>
                    Close
                </button>
                <ButtonLoader
                    type="submit"
                    className={'btn btn-primary'}
                    disabled={isLoading}
                    isLoading={isLoading}
                    loaderButtonText={''}
                    buttonText={'Print'}
                    onClick={() =>
                        props.mode == ReceiptViewMode.TransactionView
                            ? fetchReceiptByTransactionId()
                            : fetchReceiptByTransactionIds()
                    }
                />
            </div>
        );
    };

    const renderLoader = () => {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                }}>
                <Loader active={isLoading} type="ball-scale-multiple" />
            </div>
        );
    };

    const renderReceipts = () => {
        return transactions?.map((trx, idx) => (
            <div key={idx} className="receipt mb-5">
                {generateReceiptLines(trx)}
                {generateTransactionDetails(trx)}
            </div>
        ));
    };

    return (
        <Rodal
            visible={props.isVisible && doesReceiptExist}
            onClose={props.onClose}
            className="receipt-popup"
            width={500}
            animation="fade"
            closeMaskOnClick={false}
            centered>
            <ModalBody>
                {transactions == null ? (
                    renderLoader()
                ) : (
                    <div className="receipt">
                        {generateHeader()}
                        {renderReceipts()}
                        {generateFooter()}
                    </div>
                )}
            </ModalBody>
        </Rodal>
    );
};
